import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { ReactComponent as DropdownIcon } from "../../assets/images/dropdown.svg";
import * as api from "../../api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");

const SlotSelector = ({
  eventPk,
  tierPk,
  eventStartDate,
  eventEndDate,
  maxDaysToShow = null,
  selectedSlot = null,
  onSlotSelect = () => { },
  currency = "",
}) => {
  const eventStart = useMemo(
    () => (eventStartDate ? new Date(eventStartDate) : null),
    [eventStartDate]
  );
  const eventEnd = useMemo(
    () => (eventEndDate ? new Date(eventEndDate) : null),
    [eventEndDate]
  );
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);
  const parsePositiveInteger = useCallback((value) => {
    if (value == null || value === "") return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.floor(parsed);
  }, []);
  const maxDaysToShowLimit = useMemo(
    () => parsePositiveInteger(maxDaysToShow),
    [maxDaysToShow, parsePositiveInteger]
  );
  const selectableStartDate = useMemo(() => {
    const eventStartDay = eventStart
      ? new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate())
      : null;
    if (eventStartDay && eventStartDay >= today) {
      return eventStartDay;
    }
    return new Date(today.getTime());
  }, [eventStart, today]);
  const selectableEndDate = useMemo(() => {
    const eventEndDay = eventEnd
      ? new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate())
      : null;
    const daysLimitEnd = maxDaysToShowLimit
      ? (() => {
        const d = new Date(selectableStartDate.getTime());
        d.setDate(d.getDate() + maxDaysToShowLimit - 1);
        return d;
      })()
      : null;

    if (eventEndDay && daysLimitEnd) {
      return eventEndDay < daysLimitEnd ? eventEndDay : daysLimitEnd;
    }
    return eventEndDay || daysLimitEnd;
  }, [eventEnd, maxDaysToShowLimit, selectableStartDate]);
  const minMonthDate = useMemo(
    () =>
      new Date(
        selectableStartDate.getFullYear(),
        selectableStartDate.getMonth(),
        1
      ),
    [selectableStartDate]
  );
  const maxMonthDate = useMemo(
    () =>
      selectableEndDate
        ? new Date(
          selectableEndDate.getFullYear(),
          selectableEndDate.getMonth(),
          1
        )
        : null,
    [selectableEndDate]
  );

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    if (eventStart && eventStart >= now) {
      return new Date(eventStart.getFullYear(), eventStart.getMonth(), 1);
    }
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState("");

  const scrollRef = useRef(null);
  const slotsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    setCurrentMonth((prev) => {
      const prevMonth = new Date(prev.getFullYear(), prev.getMonth(), 1);
      if (prevMonth < minMonthDate) return new Date(minMonthDate.getTime());
      if (maxMonthDate && prevMonth > maxMonthDate) {
        return new Date(maxMonthDate.getTime());
      }
      return prev;
    });
  }, [minMonthDate, maxMonthDate]);

  const updateScrollArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollArrows();
    if (!loadingSlots && slots.length > 0 && slotsRef.current) {
      setTimeout(() => {
        slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [slots, loadingSlots, updateScrollArrows]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(".slot-selector__slot-card")?.offsetWidth || 200;
    el.scrollBy({ left: dir * (cardWidth + 12), behavior: "smooth" });
  };

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, current: false, date: null });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isBeforeStart = date < selectableStartDate;
      const isAfterEnd = selectableEndDate ? date > selectableEndDate : false;
      days.push({
        day: i,
        current: true,
        date,
        isPast: isBeforeStart || isAfterEnd,
      });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, current: false, date: null });
      }
    }

    return days;
  }, [currentMonth, selectableStartDate, selectableEndDate]);

  const fetchSlotsForDate = useCallback(
    async (date) => {
      const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      setLoadingSlots(true);
      setSlotError("");
      setSlots([]);
      try {
        const response = await api.events.fetchSlots(eventPk, tierPk, dateStr);
        const results = response?.data?.results ?? response?.data ?? [];
        setSlots(Array.isArray(results) ? results : []);
      } catch (err) {
        setSlotError("Failed to load slots. Please try again.");
      } finally {
        setLoadingSlots(false);
      }
    },
    [eventPk, tierPk]
  );

  const goToPrevMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      return next < minMonthDate ? prev : next;
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      if (maxMonthDate && next > maxMonthDate) {
        return prev;
      }
      return next;
    });
  };
  const canGoPrevMonth = currentMonth > minMonthDate;
  const canGoNextMonth = maxMonthDate ? currentMonth < maxMonthDate : true;

  const handleDateClick = (dayObj) => {
    if (!dayObj.current || dayObj.isPast) return;
    setSelectedDate(dayObj.date);
    fetchSlotsForDate(dayObj.date);
  };

  const isSelected = (dayObj) => {
    if (!selectedDate || !dayObj.date) return false;
    return (
      selectedDate.getFullYear() === dayObj.date.getFullYear() &&
      selectedDate.getMonth() === dayObj.date.getMonth() &&
      selectedDate.getDate() === dayObj.date.getDate()
    );
  };

  const isToday = (dayObj) => {
    if (!dayObj.date) return false;
    const today = new Date();
    return (
      dayObj.date.getFullYear() === today.getFullYear() &&
      dayObj.date.getMonth() === today.getMonth() &&
      dayObj.date.getDate() === today.getDate()
    );
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;

    if (timeStr.includes("T") || timeStr.includes(" ")) {
      const d = new Date(timeStr);
      if (Number.isNaN(d.getTime())) return null;
      const formatted = d.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const match = formatted.trim().match(/^(.+?)\s*([AP]M)$/i);
      if (match) {
        return {
          main: match[1],
          meridiem: match[2].toUpperCase(),
        };
      }
      return { main: formatted.trim(), meridiem: "" };
    }

    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    if (Number.isNaN(hour)) return null;
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 || 12;
    return {
      main: `${h12}:${(m || "00").slice(0, 2)}`,
      meridiem: ampm,
    };
  };

  const parseNumber = (value) => {
    if (value == null) return null;
    if (typeof value === "string" && value.trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <div className="slot-selector" onClick={(e) => e.stopPropagation()}>
      {/* Calendar */}
      <div className="slot-selector__calendar">
        <div className="slot-selector__calendar-header">
          <button
            className="slot-selector__nav-btn"
            onClick={goToPrevMonth}
            type="button"
            disabled={!canGoPrevMonth}
          >
            <DropdownIcon className="slot-selector__nav-icon slot-selector__nav-icon--prev" />
          </button>
          <span className="slot-selector__month-label">
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button
            className="slot-selector__nav-btn"
            onClick={goToNextMonth}
            type="button"
            disabled={!canGoNextMonth}
          >
            <DropdownIcon className="slot-selector__nav-icon slot-selector__nav-icon--next" />
          </button>
        </div>

        <div className="slot-selector__weekdays">
          {DAYS.map((d) => (
            <div key={d} className="slot-selector__weekday">
              {d}
            </div>
          ))}
        </div>

        <div className="slot-selector__days">
          {calendarDays.map((dayObj, idx) => (
            <button
              key={idx}
              type="button"
              className={
                "slot-selector__day" +
                (!dayObj.current ? " slot-selector__day--outside" : "") +
                (dayObj.isPast ? " slot-selector__day--past" : "") +
                (!dayObj.isPast && dayObj.current
                  ? " slot-selector__day--available"
                  : "") +
                (isSelected(dayObj) ? " slot-selector__day--selected" : "") +
                (isToday(dayObj) ? " slot-selector__day--today" : "")
              }
              onClick={() => handleDateClick(dayObj)}
              disabled={!dayObj.current || dayObj.isPast}
            >
              {dayObj.day}
              {isToday(dayObj) && !isSelected(dayObj) && (
                <span className="slot-selector__day-dot slot-selector__day-dot--today"></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Slots Area */}
      {selectedDate && (
        <div className="slot-selector__slots" ref={slotsRef}>
          <p className="slot-selector__slots-title">
            Available Slots &mdash;{" "}
            {selectedDate.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </p>

          {loadingSlots && (
            <div className="slot-selector__loading">
              <div className="slot-selector__spinner"></div>
              <span>Loading slots&hellip;</span>
            </div>
          )}

          {slotError && (
            <p className="slot-selector__error">{slotError}</p>
          )}

          {!loadingSlots && !slotError && slots.length === 0 && (
            <p className="slot-selector__no-slots">
              No slots available for this date.
            </p>
          )}

          {!loadingSlots && !slotError && slots.length > 0 && (
            <div className="slot-selector__slots-scroll-wrapper">
              {canScrollLeft && (
                <button
                  type="button"
                  className="slot-selector__scroll-arrow slot-selector__scroll-arrow--left"
                  onClick={() => scrollBy(-1)}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 6l-6 6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
                  </svg>
                </button>
              )}
              <div
                className="slot-selector__slots-grid"
                ref={scrollRef}
                onScroll={updateScrollArrows}
              >
                {slots.map((slot) => {
                  const slotKey = slot.id ?? slot.pk;
                  const availableCapacity = parseNumber(
                    slot.availableCapacity ?? slot.available_capacity
                  );
                  const totalCapacity = parseNumber(
                    slot.capacity ?? slot.totalCapacity ?? slot.total_capacity
                  );
                  const isSoldOutFlag = Boolean(
                    slot.isFull ?? slot.is_full ?? slot.soldOut ?? slot.sold_out
                  );
                  const isFull =
                    isSoldOutFlag ||
                    (availableCapacity != null && availableCapacity <= 0) ||
                    (totalCapacity != null && totalCapacity <= 0);
                  const isActive =
                    (selectedSlot?.id ?? selectedSlot?.pk) === slotKey;
                  const startTime = formatTime(
                    slot.start_time || slot.startTime || slot.time || slot.date
                  );
                  const endTime =
                    slot.end_time || slot.endTime
                      ? formatTime(slot.end_time || slot.endTime)
                      : null;

                  return (
                    <button
                      key={slotKey}
                      type="button"
                      className={
                        "slot-selector__slot-card" +
                        (isActive ? " slot-selector__slot-card--selected" : "") +
                        (isFull ? " slot-selector__slot-card--full" : "")
                      }
                      onClick={() => {
                        if (isFull) return;
                        onSlotSelect(slot);
                      }}
                      disabled={isFull}
                    >
                      <span className="slot-selector__slot-card-glow"></span>

                      <span className="slot-selector__slot-time-block">
                        <span className="slot-selector__slot-time">
                          <span className="slot-selector__slot-time-main">{startTime?.main || ""}</span>
                          {startTime?.meridiem && (
                            <span className="slot-selector__slot-time-meridiem">{startTime.meridiem}</span>
                          )}
                        </span>
                        {endTime?.main && (
                          <span className="slot-selector__slot-end-time">
                            <span className="slot-selector__slot-time-separator">&ndash;</span>
                            <span className="slot-selector__slot-end-time-main">{endTime.main}</span>
                            {endTime?.meridiem && (
                              <span className="slot-selector__slot-time-meridiem">{endTime.meridiem}</span>
                            )}
                          </span>
                        )}
                      </span>

                      {slot.price != null && (
                        <span className="slot-selector__slot-price">
                          {(slot.price / 100).toLocaleString()} {currency}
                        </span>
                      )}

                      {availableCapacity != null && (
                        <span className="slot-selector__slot-spots">
                          <span
                            className="slot-selector__slot-spots-bar"
                            style={{
                              width: `${Math.min(
                                100,
                                (availableCapacity /
                                  (totalCapacity || availableCapacity || 1)) *
                                100
                              )}%`,
                            }}
                          ></span>
                          <span className="slot-selector__slot-spots-text">
                            {isFull
                              ? "Full"
                              : `${availableCapacity} spot${availableCapacity !== 1 ? "s" : ""} left`}
                          </span>
                        </span>
                      )}

                    </button>
                  );
                })}
              </div>
              {canScrollRight && (
                <button
                  type="button"
                  className="slot-selector__scroll-arrow slot-selector__scroll-arrow--right"
                  onClick={() => scrollBy(1)}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 6l6 6-6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SlotSelector;
